'use client';

import React, { useRef } from 'react';
import { 
  ArrowLeft, 
  Sun, 
  CloudSun, 
  Calendar as CalendarIcon, 
  Trash2, 
  Plus, 
  ChevronRight, 
  ChevronLeft,
  Tag,
  Clock,
  Sparkles
} from 'lucide-react';
import { Service, Staff, TimeSlot, DateItem } from './types';
import { getThemeTokens } from '@/lib/themeTokens';

interface ScheduleViewProps {
  businessName?: string;
  portalTheme?: string;
  brandColor?: string;
  primaryService: Service;
  selectedServices: Service[];
  allServices: Service[];
  assignedStaffName: string;
  availableDates: DateItem[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
  slots: TimeSlot[];
  loadingSlots: boolean;
  selectedTime: string;
  onSelectTime: (time: string) => void;
  onAddServiceToCart: (service: Service) => void;
  onRemoveServiceFromCart: (serviceId: string) => void;
  onProceedToClientData: () => void;
  onBack: () => void;
}

export const ScheduleView: React.FC<ScheduleViewProps> = ({
  businessName = 'Reservá tu turno',
  portalTheme,
  brandColor,
  primaryService,
  selectedServices,
  allServices,
  assignedStaffName,
  availableDates,
  selectedDate,
  onSelectDate,
  slots,
  loadingSlots,
  selectedTime,
  onSelectTime,
  onAddServiceToCart,
  onRemoveServiceFromCart,
  onProceedToClientData,
  onBack,
}) => {
  const datesScrollRef = useRef<HTMLDivElement>(null);
  const theme = getThemeTokens(portalTheme, brandColor);

  const totalCashPrice = selectedServices.reduce((acc, s) => acc + s.cashPrice, 0);
  const totalDuration = selectedServices.reduce((acc, s) => acc + s.duration, 0);

  // Separar slots en Mañana (antes de 13:00) y Tarde (13:00 en adelante)
  const morningSlots = slots.filter((s) => {
    const hour = parseInt(s.time.split(':')[0], 10);
    return hour < 13;
  });

  const afternoonSlots = slots.filter((s) => {
    const hour = parseInt(s.time.split(':')[0], 10);
    return hour >= 13;
  });

  // Servicios complementarios para cross-selling ("Sumá otro servicio")
  const complementaryServices = allServices.filter(
    (s) => !selectedServices.some((sel) => sel.id === s.id)
  );

  // Formatear fecha actual elegida (ej. "Mar 13 De Oct")
  const currentDateObj = availableDates.find((d) => d.fullDate === selectedDate);
  const formattedDateString = currentDateObj
    ? `${currentDateObj.dayName} ${currentDateObj.dayNum} De ${currentDateObj.monthShort}`
    : selectedDate;

  // Desplazar cinta de fechas
  const scrollDates = (direction: 'left' | 'right') => {
    if (datesScrollRef.current) {
      const offset = direction === 'left' ? -200 : 200;
      datesScrollRef.current.scrollBy({ left: offset, behavior: 'smooth' });
    }
  };

  return (
    <div style={{ color: theme.textPrimary }} className="w-full max-w-5xl mx-auto px-4 pb-24">
      {/* Barra superior con nombre de negocio y botón volver */}
      <div className="flex items-center justify-between mb-6 pt-2">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            aria-label="Volver"
            style={{
              backgroundColor: theme.bgSurface,
              borderColor: theme.borderSubtle,
              color: theme.textPrimary
            }}
            className="w-10 h-10 rounded-full border flex items-center justify-center hover:opacity-85 transition shadow-xs cursor-pointer shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1
            style={{ color: theme.textPrimary }}
            className="text-xl sm:text-2xl font-bold tracking-tight"
          >
            {businessName}
          </h1>
        </div>

        <div
          style={{
            backgroundColor: theme.brandButtonBg,
            color: theme.brandButtonText
          }}
          className="w-9 h-9 rounded-full flex items-center justify-center shadow-xs"
        >
          <CalendarIcon className="w-4 h-4" />
        </div>
      </div>

      {/* Layout en 2 columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Columna Izquierda: Calendario Horizontal y Grilla de Slots */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          {/* Cinta horizontal de días con flechas de desplazamiento */}
          <div className="relative flex items-center">
            <button
              onClick={() => scrollDates('left')}
              style={{
                backgroundColor: theme.bgSurface,
                borderColor: theme.borderSubtle,
                color: theme.textPrimary
              }}
              className="hidden sm:flex w-8 h-8 rounded-full border items-center justify-center hover:opacity-80 transition shadow-xs cursor-pointer z-10 shrink-0 mr-1"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div
              ref={datesScrollRef}
              className="flex items-center gap-2.5 overflow-x-auto py-1 px-1 scrollbar-none w-full"
            >
              {availableDates.map((d) => {
                const isSelected = d.fullDate === selectedDate;
                return (
                  <button
                    key={d.fullDate}
                    onClick={() => {
                      onSelectDate(d.fullDate);
                      onSelectTime(''); // reiniciar slot al cambiar de día
                    }}
                    style={{
                      backgroundColor: isSelected ? theme.brandButtonBg : theme.bgSurface,
                      color: isSelected ? theme.brandButtonText : theme.textPrimary,
                      borderColor: isSelected ? theme.brandColor : theme.borderSubtle
                    }}
                    className="shrink-0 w-16 sm:w-20 py-2.5 rounded-xl sm:rounded-2xl border flex flex-col items-center justify-center transition duration-150 cursor-pointer shadow-2xs"
                  >
                    <span
                      style={{ opacity: isSelected ? 0.9 : 0.7 }}
                      className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider"
                    >
                      {d.dayName}
                    </span>
                    <span className="text-base sm:text-lg font-bold my-0.5">
                      {d.dayNum}
                    </span>
                    <span
                      style={{ opacity: isSelected ? 0.8 : 0.6 }}
                      className="text-[10px] font-semibold"
                    >
                      {d.monthShort}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => scrollDates('right')}
              style={{
                backgroundColor: theme.bgSurface,
                borderColor: theme.borderSubtle,
                color: theme.textPrimary
              }}
              className="flex w-8 h-8 rounded-full border items-center justify-center hover:opacity-80 transition shadow-xs cursor-pointer z-10 shrink-0 ml-1"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Slots Horarios */}
          <div
            style={{
              backgroundColor: theme.bgSurface,
              borderColor: theme.borderSubtle
            }}
            className="rounded-3xl p-6 shadow-xs border flex flex-col gap-6"
          >
            {loadingSlots ? (
              <div className="py-12 flex flex-col items-center justify-center gap-2" style={{ color: theme.textMuted }}>
                <div
                  style={{ borderTopColor: theme.brandColor }}
                  className="w-6 h-6 border-2 border-slate-300 rounded-full animate-spin"
                />
                <span className="text-xs font-semibold">Cargando disponibilidad en tiempo real...</span>
              </div>
            ) : slots.length === 0 ? (
              <div className="py-12 text-center text-xs" style={{ color: theme.textMuted }}>
                No hay turnos disponibles para esta fecha. Por favor elegí otro día.
              </div>
            ) : (
              <>
                {/* 1. HORARIOS POR LA MAÑANA */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: theme.textSecondary }}>
                    <Sun className="w-3.5 h-3.5 text-amber-500" />
                    <span>HORARIOS POR LA MAÑANA</span>
                  </div>

                  {morningSlots.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2.5">
                      {morningSlots.map((slot) => {
                        const isSelected = selectedTime === slot.time;
                        return (
                          <button
                            key={slot.time}
                            disabled={!slot.available}
                            onClick={() => onSelectTime(slot.time)}
                            style={{
                              backgroundColor: isSelected
                                ? theme.brandButtonBg
                                : slot.available
                                ? theme.bgSubtle
                                : theme.bgPage,
                              color: isSelected
                                ? theme.brandButtonText
                                : slot.available
                                ? theme.textPrimary
                                : theme.textMuted,
                              borderColor: isSelected
                                ? theme.brandColor
                                : theme.borderSubtle
                            }}
                            className={`py-3 rounded-xl text-xs sm:text-sm font-bold border transition duration-150 ${
                              slot.available ? 'cursor-pointer hover:opacity-90' : 'cursor-not-allowed line-through opacity-40'
                            }`}
                          >
                            {slot.time}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs italic" style={{ color: theme.textMuted }}>
                      No hay horarios disponibles por la mañana.
                    </p>
                  )}
                </div>

                <hr style={{ borderColor: theme.borderSubtle }} />

                {/* 2. HORARIOS POR LA TARDE */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: theme.textSecondary }}>
                    <CloudSun className="w-3.5 h-3.5 text-amber-600" />
                    <span>HORARIOS POR LA TARDE</span>
                  </div>

                  {afternoonSlots.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2.5">
                      {afternoonSlots.map((slot) => {
                        const isSelected = selectedTime === slot.time;
                        return (
                          <button
                            key={slot.time}
                            disabled={!slot.available}
                            onClick={() => onSelectTime(slot.time)}
                            style={{
                              backgroundColor: isSelected
                                ? theme.brandButtonBg
                                : slot.available
                                ? theme.bgSubtle
                                : theme.bgPage,
                              color: isSelected
                                ? theme.brandButtonText
                                : slot.available
                                ? theme.textPrimary
                                : theme.textMuted,
                              borderColor: isSelected
                                ? theme.brandColor
                                : theme.borderSubtle
                            }}
                            className={`py-3 rounded-xl text-xs sm:text-sm font-bold border transition duration-150 ${
                              slot.available ? 'cursor-pointer hover:opacity-90' : 'cursor-not-allowed line-through opacity-40'
                            }`}
                          >
                            {slot.time}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs italic" style={{ color: theme.textMuted }}>
                      No hay horarios disponibles por la tarde.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Columna Derecha: Tarjeta Resumen y Carrusel "Sumá otro servicio" */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          {/* Tarjeta Resumen */}
          <div
            style={{
              backgroundColor: theme.bgSurface,
              borderColor: theme.borderSubtle
            }}
            className="rounded-3xl p-6 shadow-xs border flex flex-col gap-4"
          >
            {/* Lista de servicios seleccionados con miniatura y precio */}
            <div className="flex flex-col gap-3">
              {selectedServices.map((srv) => (
                <div key={srv.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      style={{ backgroundColor: theme.bgSubtle }}
                      className="w-12 h-12 rounded-xl overflow-hidden shrink-0"
                    >
                      <img
                        src={srv.imageUrl || 'https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=200&h=200&fit=crop'}
                        alt={srv.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3
                        style={{ color: theme.textPrimary }}
                        className="text-sm font-bold truncate"
                      >
                        {srv.name}
                      </h3>
                      <p style={{ color: theme.textSecondary }} className="text-xs">
                        👤 con {assignedStaffName}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      style={{ color: theme.textPrimary }}
                      className="font-bold text-sm whitespace-nowrap"
                    >
                      ${' '}
                      {srv.cashPrice.toLocaleString('es-AR', {
                        minimumFractionDigits: 0,
                      })}
                    </span>
                    {selectedServices.length > 1 && (
                      <button
                        onClick={() => onRemoveServiceFromCart(srv.id)}
                        className="text-slate-400 hover:text-rose-500 p-1 cursor-pointer transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Fecha y hora seleccionada si existe */}
            {selectedTime && (
              <div
                style={{
                  backgroundColor: theme.bgSubtle,
                  borderColor: theme.borderSubtle
                }}
                className="border rounded-xl p-3 flex items-center justify-between text-xs font-semibold"
              >
                <span style={{ color: theme.textMuted }}>Fecha y hora</span>
                <span style={{ color: theme.textPrimary }} className="font-bold">
                  {formattedDateString} · {selectedTime} Hs
                </span>
              </div>
            )}

            <hr style={{ borderColor: theme.borderSubtle }} />

            {/* Fila Total */}
            <div className="flex items-center justify-between">
              <span
                style={{ color: theme.textPrimary }}
                className="text-sm font-bold flex items-center gap-1.5"
              >
                💵 Total
              </span>
              <span
                style={{ color: theme.textPrimary }}
                className="text-xl sm:text-2xl font-black"
              >
                ${' '}
                {totalCashPrice.toLocaleString('es-AR', {
                  minimumFractionDigits: 0,
                })}
              </span>
            </div>

            {/* Link cupón */}
            <button
              style={{ color: theme.textSecondary }}
              className="text-xs hover:opacity-80 flex items-center gap-1.5 self-start cursor-pointer"
            >
              <Tag className="w-3.5 h-3.5 opacity-60" />
              <span>¿Tenés un cupón?</span>
            </button>

            {/* Botón de acción: Falta elegir horario vs Continuar */}
            <button
              disabled={!selectedTime}
              onClick={onProceedToClientData}
              style={{
                backgroundColor: selectedTime ? theme.brandButtonBg : theme.bgSubtle,
                color: selectedTime ? theme.brandButtonText : theme.textMuted,
                borderColor: theme.borderSubtle
              }}
              className={`w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 border transition duration-200 shadow-xs ${
                selectedTime
                  ? 'hover:opacity-90 cursor-pointer'
                  : 'cursor-not-allowed opacity-60'
              }`}
            >
              <span>{selectedTime ? 'Continuar' : 'Falta elegir horario'}</span>
              {selectedTime && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>

          {/* Sección Cross-Selling: "Sumá otro servicio" (AppGenda Fase 2) */}
          {complementaryServices.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-extrabold text-[#0f172a] text-sm">
                  Sumá otro servicio
                </span>
                <span className="text-slate-400 font-semibold cursor-pointer hover:text-slate-600">
                  Ver todos →
                </span>
              </div>

              {/* Carrusel horizontal de servicios complementarios con botón '+' */}
              <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-none">
                {complementaryServices.map((comp) => (
                  <div
                    key={comp.id}
                    className="relative shrink-0 w-36 h-40 rounded-2xl overflow-hidden shadow-sm border border-slate-200 group bg-slate-900"
                  >
                    <img
                      src={
                        comp.imageUrl ||
                        'https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=300&h=300&fit=crop'
                      }
                      alt={comp.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

                    {/* Botón circular '+' arriba a la derecha */}
                    <button
                      onClick={() => onAddServiceToCart(comp)}
                      aria-label={`Sumar ${comp.name}`}
                      className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-black/60 hover:bg-emerald-600 text-white flex items-center justify-center backdrop-blur-sm transition cursor-pointer shadow-md"
                    >
                      <Plus className="w-4 h-4" />
                    </button>

                    {/* Título y precio al pie */}
                    <div className="absolute bottom-2.5 left-2.5 right-2.5 text-white">
                      <p className="text-[11px] font-bold leading-tight line-clamp-2">
                        {comp.name}
                      </p>
                      <p className="text-[11px] font-extrabold text-emerald-400 mt-0.5">
                        ${' '}
                        {comp.cashPrice.toLocaleString('es-AR', {
                          minimumFractionDigits: 0,
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
